import { useFormik } from "formik";
import React, { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import * as yup from "yup";
import driverLoginBg from "../assets/login-bg.svg";
import logo from "../assets/logo-prodrive.svg";
import LSVariables from "../configs/LS_variables.json";
import { AuthService } from "../service/AuthService";
import { setSavedState } from "../utils/LocalStorage.utils";

const Signup: React.FC = () => {
  const [isLoadingSignup, setIsSignupLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const location = useLocation();
  const authService = new AuthService();

  // Get referral code from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const referralCodeFromUrl =
    searchParams.get("code")?.toUpperCase() || "";

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Veuillez entrer une adresse e-mail valide")
      .required("L'e-mail est requis"),
    password: yup
      .string()
      .min(8, "Le mot de passe doit contenir au moins 8 caractères")
      .required("Le mot de passe est requis"),
    name: yup.string().required("Le nom est requis"),
    referralCode: yup
      .string()
      .matches(/^[A-Z0-9]{8}$/, "Code de parrainage invalide")
      .optional(),
    SIRETNumber: yup
      .number()
      .when('referralCode', {
        is: (val: string) => val && val.length > 0,
        then: yup.number()
          .required("Le numéro SIRET est requis pour le parrainage")
          .test('len', 'Le numéro SIRET doit contenir 14 chiffres', 
            val => val?.toString().length === 14),
        otherwise: yup.number().optional()
      }),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      referralCode: referralCodeFromUrl,
      SIRETNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSignupLoading(true);
      try {
        const signupData = {
          email: values.email,
          password: values.password,
          name: values.name,
          ...(values.referralCode && { 
            referralCode: values.referralCode,
            SIRETNumber: Number(values.SIRETNumber)
          }),
        };

        const signupRes = await authService.signup(signupData);
        if (signupRes && signupRes.data) {
          // Perform login immediately after successful signup
          const loginRes = await authService.logIn({
            email: values.email,
            password: values.password,
          });
          if (loginRes && loginRes.data) {
            setSavedState(loginRes.data.data, LSVariables.saved_user);
            history.push("/drivers");
          } else {
            throw new Error("Login failed after signup");
          }
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            "Une erreur s'est produite lors de l'inscription"
        );
      } finally {
        setIsSignupLoading(false);
      }
    },
  });

  // Show SIRET field only when referral code is present
  const showSIRETField = formik.values.referralCode.length > 0;

  return (
    <div className="flex h-screen">
      {/* Left side - Signup Form */}
      <div className="md:w-1/2 mx-auto md:m flex items-center justify-center">
        <div className="w-full max-w-[500px]">
          <div className="py-12">
            <div className="text-center mb-8">
              <img
                src={logo}
                alt="ProDrive Logo"
                className="mx-auto h-12 mb-4"
              />
              <h2 className="text-2xl font-semibold text-gray-800">
                Inscription
              </h2>
            </div>
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div>
                <input
                  id="name"
                  type="text"
                  placeholder="Nom"
                  autoComplete="name"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.name && formik.errors.name
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("name")}
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.name}
                  </p>
                )}
              </div>
              <div>
                <input
                  id="email"
                  type="email"
                  placeholder="E-mail"
                  autoComplete="email"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("email")}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div>
                <input
                  id="password"
                  type="password"
                  placeholder="Mot de passe"
                  autoComplete="new-password"
                  required
                  className={`w-full px-3 py-4 border ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                  {...formik.getFieldProps("password")}
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.password}
                  </p>
                )}
              </div>

              <div>
                <input
                  id="referralCode"
                  type="text"
                  placeholder="Code de parrainage (optionnel)"
                  className={`w-full px-3 py-4 border ${
                    formik.touched.referralCode && formik.errors.referralCode
                      ? "border-red-300"
                      : "border-gray-300"
                  } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light uppercase`}
                  {...formik.getFieldProps("referralCode")}
                />
                {formik.touched.referralCode && formik.errors.referralCode && (
                  <p className="mt-1 text-sm text-red-600">
                    {formik.errors.referralCode}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Si vous avez été parrainé, entrez le code ici
                </p>
              </div>

              {/* SIRET field - only shown when referral code is present */}
              {showSIRETField && (
                <div>
                  <input
                    id="SIRETNumber"
                    type="text"
                    placeholder="Numéro SIRET (requis pour le parrainage)"
                    className={`w-full px-3 py-4 border ${
                      formik.touched.SIRETNumber && formik.errors.SIRETNumber
                        ? "border-red-300"
                        : "border-gray-300"
                    } rounded-2xl focus:outline-none focus:ring-1 focus:ring-primary-light`}
                    {...formik.getFieldProps("SIRETNumber")}
                  />
                  {formik.touched.SIRETNumber && formik.errors.SIRETNumber && (
                    <p className="mt-1 text-sm text-red-600">
                      {formik.errors.SIRETNumber}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Le numéro SIRET est requis pour participer au programme de parrainage
                  </p>
                </div>
              )}

              {error && <p className="text-sm p-error">{error}</p>}

              <div>
                <button
                  type="submit"
                  disabled={isLoadingSignup}
                  className="w-full py-4 px-4 border border-transparent rounded-2xl shadow-sm text-md font-medium text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light transition duration-150 ease-in-out"
                >
                  {isLoadingSignup ? "Inscription en cours..." : "S'inscrire"}
                </button>
              </div>
            </form>
            <p className="mt-4 text-sm text-gray-600 text-center">
              Vous pourrez compléter votre profil après l'inscription.
            </p>
            <p className="mt-4 text-sm text-gray-600 text-center">
              Avez-vous un compte ?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Connectez-vous ici
              </Link>
            </p>
          </div>

          {/* Added interface transporteur text and copyright */}
          <div className="text-center mt-8">
            <p className="text-lg font-semibold text-gray-700">
              Interface transporteur
            </p>
            <p className="text-sm text-gray-500 mt-2">
              &copy; {new Date().getFullYear()} Master D. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
      {/* Right side - Background Image */}
      <div className="md:flex md:w-1/2 overflow-clip bg-slate-50">
        <img src={driverLoginBg} alt="" className="object-cover scale-125" />
      </div>
    </div>
  );
};

export default Signup;
